import React, { useEffect } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import AppPaths from '../../AppPaths';
import { useHardwareFirstQuiz } from '../../components/HardwareFirstQuiz/HardwareFirstQuizContext';

/** Names the link that opened the quiz, alongside the other quiz triggers. */
export const DEVICE_QUIZ_URL_TRIGGER = 'ecom_devicequiz_url';

interface DeviceQuizState {
  openDeviceQuiz?: boolean;
}

/**
 * `/devicequiz` (or `/shop/devicequiz`) — a shareable link to the shop with its quiz sheet already open. The sheet has no
 * url of its own, so this route hands off to `/shop` and asks it to open the sheet on arrival. The
 * ask rides in router state rather than the query string, so a refresh or a copied `/shop` url does
 * not reopen the quiz, and utm / coupon params pass through untouched (including a `products=`
 * lineup, e.g. `/devicequiz?products=mini,s3`). Visitors the shop does not
 * cover (partner, rescue, nano, unsupported markets) follow the shop's own redirect to `/products`,
 * which drops the state, so no quiz opens there.
 */
export default function DeviceQuizRoute() {
  const { search } = useLocation();
  const state: DeviceQuizState = { openDeviceQuiz: true };
  return <Redirect to={{ pathname: AppPaths.Shop, search, state }} />;
}

/**
 * Renders nothing; the landing half of `/devicequiz`, mounted by the shop. Opens the quiz sheet once,
 * then clears the router state so a back-navigation to the shop does not open it again. `available`
 * is false when the lineup hides a tracker the quiz could recommend; the state is still cleared, and
 * the shopper lands on the narrowed shop with no quiz.
 */
export function DeviceQuizAutoOpen({ available }: { available: boolean }) {
  const history = useHistory();
  const location = useLocation<DeviceQuizState | undefined>();
  const { open } = useHardwareFirstQuiz();
  const shouldOpen = !!location.state?.openDeviceQuiz;

  useEffect(() => {
    if (!shouldOpen) {
      return;
    }
    history.replace({ pathname: location.pathname, search: location.search });
    if (available) {
      open(DEVICE_QUIZ_URL_TRIGGER);
    }
  }, [shouldOpen, available, history, location.pathname, location.search, open]);

  return null;
}
